// Admin: detalle de un lead con datos de contacto, empresa, observaciones y solicitudes de cotización.
// Permite editar cualquier dato para que el admin pueda analizar y dar seguimiento al lead.

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLead, useUpdateLead, useQuotationRequests } from '@/hooks/use-store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save, Loader2, FileText } from 'lucide-react';
import type { Lead } from '@/lib/store-types';
import { normalizeQuotationRequestStatus } from '@/lib/store-types';
import type { QuotationRequestStatus } from '@/lib/store-types';

const STATUS_LABELS: Record<QuotationRequestStatus, string> = {
  draft: 'Borrador',
  pending: 'Enviado',
  en_revision: 'En revisión',
  cotizado: 'Cotizado',
  finished: 'Finalizado',
  pending_deposit_payment: 'Pendiente pago depósito',
  in_process: 'En proceso',
  shipped: 'En camino',
  pending_final_payment: 'Pend. pago balance',
  arriving_soon: 'En camino',
  delivered: 'Entregado',
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function quotationNumber(id: string) {
  return '#' + id.slice(0, 8).toUpperCase();
}

export default function AdminLeadDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: lead, isLoading: loadingLead, error } = useLead(id ?? null);
  const { data: allRequests } = useQuotationRequests();
  const updateLead = useUpdateLead();
  const { toast } = useToast();

  // Form state (todos los campos editables). Un solo teléfono con código de país; checkbox indica si tiene WhatsApp.
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneHasWhatsapp, setPhoneHasWhatsapp] = useState(false);
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [observations, setObservations] = useState('');

  useEffect(() => {
    if (!lead) return;
    setName(lead.name ?? '');
    setEmail(lead.email ?? '');
    setPhone(lead.phone ?? '');
    setPhoneHasWhatsapp(!!(lead.whatsapp?.trim()));
    setCompany(lead.company ?? '');
    setCountry(lead.country ?? '');
    setCity(lead.city ?? '');
    setObservations(lead.observations ?? '');
  }, [lead]);

  const handleSave = () => {
    if (!id) return;
    const payload: Partial<Lead> & { id: string } = {
      id,
      name: name || undefined,
      email: email || undefined,
      phone: phone || undefined,
      whatsapp: phoneHasWhatsapp && phone?.trim() ? phone.trim() : undefined,
      company: company || undefined,
      country: country || undefined,
      city: city || undefined,
      observations: observations || undefined,
    };
    updateLead.mutate(payload, {
      onSuccess: () => toast({ title: 'Lead actualizado' }),
      onError: (e) => toast({ title: 'Error al guardar', description: String(e), variant: 'destructive' }),
    });
  };

  const requests = (allRequests ?? []).filter((r) => r.lead_id === id);

  if (loadingLead || !id) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando...
      </div>
    );
  }
  if (error || !lead) {
    return (
      <div>
        <p className="text-destructive">No se encontró el lead.</p>
        <Button variant="outline" asChild className="mt-2">
          <Link to="/store/admin/leads">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Leads
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/store/admin/leads">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Leads
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Lead: {lead.name || lead.email || lead.id}</h1>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Datos de contacto */}
        <Card>
          <CardHeader>
            <CardTitle>Datos de contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@ejemplo.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono (con código de país)</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ej: +34 612 345 678"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="phone_has_whatsapp"
                checked={phoneHasWhatsapp}
                onCheckedChange={(v) => setPhoneHasWhatsapp(v === true)}
              />
              <Label htmlFor="phone_has_whatsapp" className="font-normal cursor-pointer">
                Este teléfono tiene WhatsApp para contactar rápido
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Datos de empresa (el lead suele ser trabajador de una empresa) */}
        <Card>
          <CardHeader>
            <CardTitle>Datos de empresa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company">Empresa</Label>
              <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Empresa" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">País</Label>
              <Input id="country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="País" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Ciudad</Label>
              <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ciudad" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Observaciones: para que el admin pueda analizar el lead en profundidad */}
      <Card>
        <CardHeader>
          <CardTitle>Observaciones</CardTitle>
          <p className="text-sm text-muted-foreground">Notas y análisis del admin sobre el lead (seguimiento, prioridad, etc.).</p>
        </CardHeader>
        <CardContent>
          <Textarea
            id="observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            placeholder="Observaciones..."
            rows={4}
            className="resize-y"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateLead.isPending}>
          {updateLead.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Guardar cambios
        </Button>
      </div>

      {/* Solicitudes de cotización de este lead */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Solicitudes de cotización
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-muted-foreground">Este lead no tiene solicitudes de cotización.</p>
          ) : (
            <ul className="space-y-2">
              {requests.map((qr) => (
                <li key={qr.id}>
                  <Link to={`/store/admin/quotation-requests/${qr.id}`} className="text-primary hover:underline flex items-center gap-2">
                    {quotationNumber(qr.id)} — {formatDate(qr.created_at)} — {STATUS_LABELS[normalizeQuotationRequestStatus(qr.status)]}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
